import FailedTransfersList from '@/components/payouts/failure/FailedTransfersList';
import PaymentNotChargedNotice from '@/components/payouts/failure/PaymentNotChargedNotice';
import type { InvolvedAffiliate, Transfer } from '@/types/payout';
import { Alert, Button, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface PayoutTransfersFailedAlertProps {
  failedTransfers: Transfer[];
  totalTransfers: number;
  involvedAffiliates: InvolvedAffiliate[];
  /** Instant of the last refused payment attempt, null when none was refused. */
  lastPaymentFailedAt: string | null;
  /** The provider's reason for the refusal, null when it gave none. */
  lastPaymentFailureCode: string | null;
}

/**
 * The batch failed and nothing was charged: the commissions went back to the
 * pool, so a new payout is the way forward.
 */
const PayoutTransfersFailedAlert: React.FC<PayoutTransfersFailedAlertProps> = ({
  failedTransfers,
  totalTransfers,
  involvedAffiliates,
  lastPaymentFailedAt,
  lastPaymentFailureCode,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const allFailed = failedTransfers.length === totalTransfers;

  const handleCreateNewPayout = () => {
    navigate('/payments/commissions');
  };

  return (
    <Alert.Root status="error" variant="subtle">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>
          {allFailed ? t('payouts.failure.alertTitleAll') : t('payouts.failure.alertTitle')}
        </Alert.Title>
        <Alert.Description>
          <VStack align="start" gap={3} mt={2}>
            <Text>
              {allFailed
                ? t('payouts.failure.summaryAll', { count: totalTransfers })
                : t('payouts.failure.summary', {
                    failed: failedTransfers.length,
                    total: totalTransfers,
                  })}
            </Text>

            {lastPaymentFailedAt && (
              <PaymentNotChargedNotice failureCode={lastPaymentFailureCode} />
            )}

            <FailedTransfersList
              failedTransfers={failedTransfers}
              involvedAffiliates={involvedAffiliates}
            />

            <Text color="fg.muted" fontSize="sm">
              {t('payouts.failure.description')}
            </Text>

            <Button size="sm" colorPalette="red" onClick={handleCreateNewPayout}>
              {t('payouts.failure.createNewPayout')}
            </Button>
          </VStack>
        </Alert.Description>
      </Alert.Content>
    </Alert.Root>
  );
};

export default PayoutTransfersFailedAlert;
